import { useEffect, useMemo, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import { Link } from "react-router-dom";
import TaskSkeleton from "./TaskSkeleton";

// Same year → same light row background
const YEAR_ROW_COLORS = [
    "#c8e6c9", // green
    "#bbdefb", // blue
    "#fff59d", // yellow
    "#e1bee7", // purple
    "#b2dfdb", // teal
    "#f8bbd0", // pink
];

export default function ClassList() {
    const { http, user } = AuthUser();
    const [tasks, setTasks] = useState([]);
    const [date, setDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(null);
    const [loginUser, setloginUser] = useState(user.department_id);
    const [editColumn, setEditColumn] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [colorMap, setColorMap] = useState({});

    // List of more distinctive background colors for different class groups (blink / left bar)
    const backgroundColors = [
        '#FFB3B3', // Light Red
        '#B3FFB3', // Light Green  
        '#B3B3FF', // Light Blue
        '#FFB3FF', // Light Purple
        '#FFFFB3', // Light Yellow
        '#B3FFFF', // Light Cyan
        '#FFCCB3', // Light Orange
        '#CCB3FF', // Light Lavender
        '#FFE4B3', // Light Peach
        '#B3FFE4', // Light Mint
    ];

    // Function to generate color mapping for class names (blink / left bar — unchanged)
    const generateColorMap = (tasks) => {
        const newColorMap = {};
        const classCount = {};
        let colorIndex = 0;
        
        // First pass: Count occurrences of each class
        tasks.forEach(task => {
            const normalizedClassName = task.class_name ? task.class_name.toString().trim().toLowerCase() : '';
            if (normalizedClassName) {
                classCount[normalizedClassName] = (classCount[normalizedClassName] || 0) + 1;
            }
        });
        
        // Second pass: Assign colors based on uniqueness
        tasks.forEach(task => {
            const normalizedClassName = task.class_name ? task.class_name.toString().trim().toLowerCase() : '';
            
            if (normalizedClassName && !newColorMap[normalizedClassName]) {
                // If class appears only once, make it green (we'll handle blinking in the render)
                if (classCount[normalizedClassName] === 1) {
                    newColorMap[normalizedClassName] = '#00FF00'; // Bright green for unique classes
                } else {
                    // Use regular colors for classes that appear multiple times
                    newColorMap[normalizedClassName] = backgroundColors[colorIndex % backgroundColors.length];
                    colorIndex++;
                }
            }
        });
        
        setColorMap(newColorMap);
    };

    // Same Year → same light row background
    const yearBgMap = useMemo(() => {
        const map = {};
        let i = 0;
        tasks.forEach((t) => {
            const y = (t.ext_3 || "Unknown").toString().trim() || "Unknown";
            if (!(y in map)) {
                map[y] = YEAR_ROW_COLORS[i % YEAR_ROW_COLORS.length];
                i += 1;
            }
        });
        return map;
    }, [tasks]);

    // Newest year first; same year stays together
    const displayTasks = useMemo(() => {
        return [...tasks].sort((a, b) => {
            const ya = parseInt(String(a.ext_3 || "").split("-")[0], 10) || 0;
            const yb = parseInt(String(b.ext_3 || "").split("-")[0], 10) || 0;
            if (yb !== ya) return yb - ya;
            return String(a.class_name || "").localeCompare(String(b.class_name || ""));
        });
    }, [tasks]);

    const [editUser, setEditUser] = useState({});
    useEffect(() => {
        fetchTaskDetails();
    }, []);


    const fetchTaskDetails = () => {
        setIsLoading(true);
        let endpoint = `/class?company_id=${user.company_id}`;

        http
            .get(endpoint)
            .then((res) => {
                setIsLoading(false);
                if (Array.isArray(res.data.data)) {
                    setTasks(res.data.data);
                    generateColorMap(res.data.data); // Generate color mapping when tasks are fetched
                } else {
                    setIsLoading(false);
                    console.error("API response is not an array");
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching tasks:", error);
            });
    };

    const statusUpdate = (e) => {
        setCurrentStatus(e.target.value);
    };

    const saveEditedCustemer = async (e) => {
        e.preventDefault();
        try {
            if (!editUser) {
                // Handle the case when editUser is null
                console.error("No user data to edit.");
                return;
            }

            const response = await http.put(`/class_edit/${editUser.id}`, formData1);
            if (Array.isArray(response.data.data)) {
                toast.success("Terms updated successfully!");
                setShowModal(false);
                fetchTaskDetails();
            } else {
                console.log("API response is not an array");
            }
        } catch (error) {
            console.error("Error updating user", error);
            toast.error("An error occurred while updating the customer.");
        }
        setEditColumn("");
    };

    const handleSave = async (taskId) => {
        if (currentStatus === null || taskId === null) {
            toast.error("Please select proper status");
            return true;
        }
        try {
            const response = await http.put(
                `task_status_update?task_id=${taskId}&status=${encodeURIComponent(
                    currentStatus
                )}`
            );
            toast.success("Task status updated successfully !");
            fetchTaskDetails();
        } catch (error) {
            console.error("Error fetching tasks:", error);
            toast.error("Please select proper item");
        }
    };

    const initialState = {
        name: "",
    };

    const handleChangeModal = (e) => {
        setFormData1({
            ...formData1,
            [e.target.name]: e.target.value,
        });
    };

    const deleteTask = async (taskId) => {
        console.log(taskId);
        await http
            .delete(`/departments_del/${taskId}`)
            .then((response) => {
                toast.success("Departments Deleted successfully !");
                fetchTaskDetails();
            })
            .catch((err) => {
                console.error("Error deleting task", err);
            });
    };

    const openEditModal = async (taskAll) => {
        setShowModal(true);
        setFormData1({
            name: taskAll.class_name || "",
        });
        setEditUser(taskAll);
    };

    const [formData1, setFormData1] = useState({
        name: "",
    });

    const closeModal = () => {
        setShowModal(false);
        setEditColumn("");
    };

    return (
        <>
            <style>
                {`
                @keyframes blink {
                    0% { opacity: 1; }
                    50% { opacity: 0.3; }
                    100% { opacity: 1; }
                }
                `}
            </style>
            <Toaster />
            {showModal && (
                <form onSubmit={saveEditedCustemer}>
                    <div className="modal" tabindex="-1" style={{ display: "block" }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Edit Class</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeModal}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="form-group  mt-3">
                                        <label> Name </label>
                                        <input
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            value={formData1.name}
                                            onChange={handleChangeModal}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}
                                    >
                                        Close
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        Save changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            )}

            <div className="container mt-1">
                <div className="row">
                    <h1>Class List</h1>
                    <Link className="add_link text-end mb-3" to="/class_add">
                        Add
                    </Link>
                </div>

                <div className="table-responsive">
                    {isLoading ? (
                        <TaskSkeleton />
                    ) : (
                        <table className="table">
                            <thead className="table-primary">
                                <tr>
                                    <th className="text-danger">No</th>
                                    <th>Class</th>
                                    <th>Division</th>
                                    <th>Year</th>

                                </tr>
                            </thead>
                            <tbody>
                                {displayTasks.map((taskuser, index) => {
                                    // Normalize the class name for color lookup (same as in generateColorMap)
                                    const normalizedClassName = taskuser.class_name ? taskuser.class_name.toString().trim().toLowerCase() : '';
                                    const assignedColor = colorMap[normalizedClassName] || '#cccccc';
                                    
                                    // Check if this is a unique class (green color means unique)
                                    const isUnique = assignedColor === '#00FF00';

                                    // Row fill by Year only — same year = same colour
                                    const rowBg =
                                        yearBgMap[(taskuser.ext_3 || "Unknown").toString().trim() || "Unknown"] || "#ffffff";
                                    const cellStyle = {
                                        backgroundColor: rowBg,
                                        ["--bs-table-bg"]: rowBg,
                                        ["--bs-table-accent-bg"]: "transparent",
                                    };
                                    
                                    return (
                                    <tr 
                                        key={taskuser.id}
                                        style={{ 
                                            borderLeft: `8px solid ${assignedColor}`,
                                            backgroundColor: rowBg,
                                            ["--bs-table-bg"]: rowBg,
                                        }}
                                    >
                                        <td style={cellStyle}>{index + 1}</td>
                                        <td style={cellStyle}>
                                            <span 
                                                style={{
                                                    display: 'inline-block',
                                                    width: '12px',
                                                    height: '12px',
                                                    backgroundColor: assignedColor,
                                                    borderRadius: '50%',
                                                    marginRight: '8px',
                                                    animation: isUnique ? 'blink 1s infinite' : 'none'
                                                }}
                                            ></span>
                                            {taskuser.class_name}
                                        </td>
                                        <td style={cellStyle}>{taskuser.ext_1}</td>
                                        <td style={cellStyle}>{taskuser.ext_3}</td>
                                        {/* <td>
                                            {user.status === 1 ? <button className='btn btn-link' onClick={() => openEditModal(taskuser)}>Edit</button> : ''}
                                        </td> */}
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </>
    );
}
